import { useContext, useEffect, useMemo } from "react";
import {
  Flex,
  ListWithPagination,
  PaginationContext,
  SearchContext,
} from "dash-ui";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import { Amenity } from "gql/generated.graphql";
import { useGetAmenitiesQuery } from "gql/generated.graphql";
import DeliveryImage from "assets/images/png/Delivery.png";
import SearchingImage from "assets/images/png/Searching.png";

import AmenityItem from "./components/AmenityItem/AmenityItem";
import { IAmenitiesList } from "./AmenitiesList.interfaces";

const AmenitiesWrapper = styled(Flex)`
  .amenities_container {
    flex-wrap: wrap;
    & > div {
      flex-grow: 1;
    }
  }
`;

const AmenitiesList = ({
  setAmenitySaveModal,
  listItemSizes,
}: IAmenitiesList) => {
  const { t } = useTranslation(["common", "tables"]);
  const {
    setTotal,
    state: paginationState,
    getRefetchProps,
  } = useContext(PaginationContext);
  const { searchSlug } = useContext(SearchContext);
  const { skip, limit } = getRefetchProps(paginationState);

  const searchByName = useMemo(
    () => (searchSlug.length > 2 ? searchSlug : ""),
    [searchSlug]
  );

  const {
    data: result,
    refetch,
    loading,
  } = useGetAmenitiesQuery({
    variables: {
      pagination: { skip, limit },
      filter: {
        ...(searchByName && { searchByName }),
        isPrimary: true,
      },
    },
    onCompleted: (result) => {
      const total = result?.getAmenities?.totalCount || 0;
      setTotal(total);
    },
  });

  const { data } = useMemo(
    () => ({
      data: result?.getAmenities?.collection || [],
    }),
    [result]
  );

  useEffect(() => {
    refetch({
      pagination: { skip, limit },
      ...(searchByName ? { searchByName } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, limit, searchByName]);

  const labelToShow = !loading
    ? searchSlug
      ? t("common:placeholders.noSearchResults")
      : t("tables:amenities.noAmenities")
    : "";

  const imageToShow = !loading
    ? searchSlug
      ? SearchingImage
      : DeliveryImage
    : undefined;

  return (
    <AmenitiesWrapper w="100%">
      <ListWithPagination
        px={6}
        listProps={{
          loading,
          data,
          grid: listItemSizes.minWidth,
          gap: listItemSizes.gap,
          containerClassName: "amenities_container",
          placeholderProps: {
            label: labelToShow,
            image: imageToShow,
          },
        }}
        paginationProps={{
          label: t("tables:amenities.paginationLabel") || "",
          previousButtonLabel: t("common:labels.previous"),
          nextButtonLabel: t("common:labels.next"),
        }}
      >
        {(amenity: Amenity) => (
          <AmenityItem
            amenity={amenity}
            onAmenityEditClick={() =>
              setAmenitySaveModal({ mode: "update", initialDoc: amenity })
            }
          />
        )}
      </ListWithPagination>
    </AmenitiesWrapper>
  );
};

export default AmenitiesList;
